<script setup lang="tsx">
import {
  Close,
  Fold,
  InfoFilled,
  Refresh,
  Setting,
  TopRight,
} from "@element-plus/icons-vue";
import localforage from "localforage";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { UserInfoDto } from "~/api";
import PreferencesViewer from "~/Compliance/components/PreferencesViewer.vue";
import RemoveRolesGroupsDialog from "~/components/RemoveRolesGroupsDialog.vue";
import { authStore } from "~/stores/authStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { AuthStoreType } from "~/types/auth";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { getEnvConfig } from "~/services/appConfig";
import {
  commitBuildInfo,
  getModeForTitle,
  refreshCaches,
  splitByCaps,
} from "~/utils/miscUtils";

const authToken = ref<string>();
const router = useRouter();

const apiBase = getEnvConfig().serverUrl;

const myInfoStore = useMyInfoStore();
const orgInfoStore = useOrgInfoStore();

const myUser = ref<UserInfoDto | null>(null);
const selectedUser = ref<UserInfoDto | null>(null);
const myGroupNames = ref<string[]>();

const showPreferencesViewer = () => {
  drawerOpen.value = true;
  nameInfoVisible.value = false;
};

const windowTitle = computed(() => {
  return myInfoStore.currentSysOrg?.name ?? "Manifast™ Business Tools";
});

const logoUrl = ref<string | null>(null);

const getLogoImageUrl = async (
  fileGuid: string | undefined | null,
): Promise<string | null> => {
  if (!authToken.value || !fileGuid) {
    return null;
  }

  try {
    const response = await fetch(`${apiBase}/api/files/${fileGuid}`, {
      headers: {
        Authorization: `Bearer ${authToken.value}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    showError(`Error getting logo for fileGuid "${fileGuid}":`, err);
    return null;
  }
};

onMounted(async () => {
  await Promise.all([myInfoStore.load(), orgInfoStore.load()]);
  authToken.value = (await authStore.getAccessTokenAsync()) ?? undefined;

  logoUrl.value = await getLogoImageUrl(orgInfoStore.MyOrgInfo?.logoFileGuid);

  document.title = windowTitle.value + getModeForTitle();

  myUser.value = {
    rowData: myInfoStore.userInfo,
  }.rowData;

  // get the names from orgGroupDtos for the group guids in the currentOrg
  myGroupNames.value = orgInfoStore.MyOrgInfo?.orgGroupDtos
    .filter((group) =>
      myInfoStore.currentSysOrg?.groupGuids.includes(group.guid),
    )
    .map((group) => group.name);
});

const otherOrganizations = computed(() => {
  return myInfoStore.mySysOrgs.filter(
    (org: any) => org.guid !== myInfoStore.currentSysOrg?.guid,
  );
});

const isSuperAdmin = computed(() => {
  return myInfoStore.userInfo?.superAdmin;
});

const isLoggingIn = ref(false);

const userLogin = async () => {
  if (isLoggingIn.value) {
    console.log("Login already in progress, ignoring duplicate request");
    return;
  }

  isLoggingIn.value = true;

  try {
    await authStore.login(AuthStoreType.User);
  } catch (err) {
    console.warn("Login failed:", err);
  } finally {
    // Reset after a delay to prevent rapid clicking
    setTimeout(() => {
      isLoggingIn.value = false;
    }, 2000);
  }
};

const handleCloseViewer = () => {
  selectedUser.value = null;
  drawerOpen.value = false;
};
const drawerOpen = ref(false);

const nameInfoVisible = ref(false);
const orgPopoverVisible = ref(false);

const isLoggingOut = ref(false);
const logout = async () => {
  if (isLoggingOut.value) {
    return;
  }
  isLoggingOut.value = true;

  sessionStorage.removeItem("currentOrgGuid");

  const token = await authStore.getAccessTokenAsync();

  if (token) {
    await apiClient.userInfo.forgetAboutMe();
  } else {
    console.warn("No Token");
  }
  await authStore.logout().then(() => {
    localforage.clear();
    isLoggingOut.value = false;
    router.push("/");
  });
};

const handleOrgChange = async (sysOrgGuid: string) => {
  sessionStorage.setItem("currentOrgGuid", sysOrgGuid);
  await refreshCaches();
  myInfoStore.clear();
  router.push("/");
};

const openInTab = (sysOrgGuid: string) => {
  orgPopoverVisible.value = false;
  window.open(`/`, "new-" + sysOrgGuid);
};

const hasNonGuestRolesForCurrentOrg = computed(() => {
  return (
    myInfoStore.currentSysOrg?.roles.filter((r) => r !== "Guest").length > 0
  );
});

const showInfo = ref(false);
const toggleInfo = () => {
  showInfo.value = !showInfo.value;
};

const removeDialogVisible = ref(false);
const showRemoveDialog = () => {
  removeDialogVisible.value = true;
  nameInfoVisible.value = false; // Close the popover
};

const handleRemoveApplied = () => {
  // Reload the page to show updated UserInfo
  location.reload();
};
</script>

<template>
  <div class="MainTopBar">
    <div class="logo" :title="commitBuildInfo">
      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="windowTitle + ' Logo'"
        @click="router.push('/')"
      />
    </div>
    <div class="title">
      {{ windowTitle }}
    </div>
    <div class="popovers">
      <div v-if="myInfoStore.isLoggedIn" class="loginButton">
        <el-popover
          v-model:visible="nameInfoVisible"
          placement="bottom-end"
          trigger="click"
          :width="400"
          popper-class="nameInfoPopover"
        >
          <div
            class="buttonsTop"
            style="display: flex; justify-content: flex-end"
          >
            <div v-if="isSuperAdmin">
              <el-tag
                v-if="isSuperAdmin"
                type="success"
                :model-value="'Super Admin'"
                >Super Admin</el-tag
              >
            </div>
            <el-link
              size="small"
              :icon="Close"
              @click="nameInfoVisible = false"
            ></el-link>
          </div>
          <el-descriptions border direction="vertical" :column="1">
            <el-descriptions-item
              v-if="myInfoStore.hasMultipleOrgs"
              :label="'Current Organization'"
            >
              {{ myInfoStore.currentSysOrg?.name }}
              <span
                class="guidDisplay"
                :title="myInfoStore.currentSysOrg?.guid"
              >
                <span v-if="showInfo">
                  {{ myInfoStore.currentSysOrg?.guid.substring(0, 8) }}
                </span>
                <el-icon class="dimInfo" @click="toggleInfo"
                  ><InfoFilled
                /></el-icon>
              </span>
              <div class="switchTo">Switch to...</div>
              <div
                v-for="(org, index) in otherOrganizations"
                :key="index"
                class="organizations-list"
              >
                <div class="orgLinkRow">
                  <el-link
                    class="orgLink"
                    underline="never"
                    title="Switch to this organization"
                    @click.prevent="handleOrgChange(org.guid)"
                  >
                    &rarr;
                    {{ org.name }}
                  </el-link>
                  <el-link
                    class="orgLink orgLink2"
                    underline="never"
                    title="Open in new tab"
                    @click.prevent="openInTab(org.guid)"
                  >
                    <el-icon><top-right /></el-icon>
                  </el-link>
                  <span class="guidDisplay" :title="org.guid">
                    <span v-if="showInfo">
                      {{ org.guid.substring(0, 8) }}
                    </span>
                    <el-icon class="dimInfo" @click="toggleInfo"
                      ><InfoFilled
                    /></el-icon>
                  </span>
                </div>
              </div>
            </el-descriptions-item>

            <el-descriptions-item
              v-if="hasNonGuestRolesForCurrentOrg || isSuperAdmin"
              label="My Roles"
            >
              <div class="usersRoles">
                <el-tag
                  v-for="role in myInfoStore.currentSysOrg?.roles"
                  :key="role"
                  :model-value="role"
                >
                  {{ splitByCaps(role) }}
                </el-tag>
              </div>
            </el-descriptions-item>

            <el-descriptions-item
              v-if="myInfoStore.currentSysOrg?.entityDto"
              label="Entity"
            >
              <div class="usersRoles">
                {{ myInfoStore.currentSysOrg?.entityDto?.name }}
              </div>
              <div class="userGuid">
                {{ myInfoStore.currentSysOrg?.entityDto?.guid.substring(0, 8) }}
              </div>
            </el-descriptions-item>

            <el-descriptions-item
              v-if="myGroupNames?.length"
              label="Entity Groups"
            >
              <div class="usersRoles">
                <el-tag
                  v-for="groupName in myGroupNames"
                  :key="groupName"
                  :model-value="groupName"
                >
                  {{ groupName }}
                </el-tag>
              </div>
            </el-descriptions-item>
            <el-descriptions-item label="Email">
              <el-text class="nowrap">
                <div>{{ myInfoStore.userInfo?.email }}</div>
                <div class="userGuid">
                  {{ myInfoStore.userInfo?.userGuid.substring(0, 8) }}
                </div>
              </el-text>
            </el-descriptions-item>
          </el-descriptions>
          <div class="buttonsBottom">
            <div>
              <el-button
                size="small"
                type="info"
                :icon="Setting"
                @click="showPreferencesViewer"
                >Preferences</el-button
              >
              <el-button size="small" :icon="Refresh" @click="refreshCaches"
                >Refresh Settings</el-button
              >
              <el-button
                size="small"
                type="danger"
                :disabled="isLoggingOut"
                @click="logout"
                >Sign out</el-button
              >
            </div>
            <div v-if="hasNonGuestRolesForCurrentOrg">
              <el-button size="small" type="warning" @click="showRemoveDialog"
                >Remove Roles or Groups</el-button
              >
            </div>
          </div>

          <template #reference>
            <span class="userInfoDisplay">
              <el-link
                v-if="myInfoStore.userInfo"
                :icon="Fold"
                underline="never"
              >
                {{ myInfoStore.userInfo?.loginName }}
              </el-link>

              <span v-else> Loading... </span>
            </span>
          </template>
        </el-popover>
      </div>

      <div v-else class="loginButton">
        <el-link @click="userLogin"> Sign in </el-link>
      </div>
    </div>
  </div>
  <PreferencesViewer
    v-if="myUser"
    :key="myUser?.userGuid"
    :drawer-open="drawerOpen"
    :adding-new-user="false"
    @drawer-closed="handleCloseViewer"
  />
  <RemoveRolesGroupsDialog
    v-model:visible="removeDialogVisible"
    @applied="handleRemoveApplied"
  />
</template>

<style lang="less">
.MainTopBar {
  height: var(--el-header-height);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  .popovers {
    justify-content: end;
    display: flex;
    gap: 3em;
  }
  .title {
    justify-self: center;
    color: var(--el-color-primary);
    font-size: 1.5rem;
    font-weight: 600;
    @media (max-width: 801px) {
      font-size: 1em;
    }
  }

  .logo {
    margin: 2px 0;
    height: calc(var(--el-header-height) * 0.8);
    display: flex;
    align-items: center;

    img {
      height: 60px;
      cursor: pointer;
      max-width: 200px;
      object-fit: contain;
    }

    span {
      color: var(--el-color-primary);
      font-weight: bold;
    }
  }

  a.el-button {
    text-decoration: none;
  }

  .loginButton {
    display: flex;
    align-items: center;
    gap: 3em;
    @media (max-width: 850px) {
      font-size: 0.9rem;
    }
    @media (max-width: 450px) {
      font-size: 0.6rem;
      .el-link__inner {
        font-size: 0.8rem;
      }
    }
    @media (min-width: 450px) and (max-width: 600px) {
      font-size: 1rem;
      .el-link__inner {
        font-size: 1rem;
      }
    }
    .userInfoDisplay {
      display: flex;
      align-items: center;
      gap: 0.5em;
      & svg {
        padding-top: 1px;
      }

      .orgName {
        margin-right: 2em;
      }
    }
  }
}

.nameInfoPopover,
.orgInfoPopover {
  .buttonsTop,
  .buttonsBottom {
    margin: 0 0 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    > div {
      display: flex;
      justify-content: right;
      gap: 5px;
    }
  }
  .buttonsBottom {
    margin: 10px 0 0;
  }
  .el-descriptions__body .el-descriptions__table .el-descriptions__content {
    padding-left: 25px;
  }
  .usersRoles {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    .el-tag {
      cursor: default;
    }
  }
  .userGuid {
    font-size: 0.8rem;
    color: var(--el-color-primary-light-8);
    justify-self: flex-end;
  }

  .organizations-list {
    max-height: 50vh;
    overflow-y: auto;
  }
  .orgLinkRow {
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 1em;
    align-items: baseline;
  }
  .switchTo {
    margin-top: 1.5em;
    font-style: italic;
  }
  .orgLink {
    cursor: pointer;
    color: #409eff;
    margin: 0.5em 0 0.5em 1.5em;
    border: 1px solid transparent;
    padding: 0 3px;
    &:hover {
      color: #307edf;
      border: 1px solid #307edf;
    }
  }
  .orgLink2 {
    margin: 0.5em 0;
  }

  .guidDisplay {
    margin: 0.5em 0 0 0;
    font-size: 0.8em;
    color: var(--el-color-primary-light-3);
    justify-self: flex-end; // when in a grid
    float: right; // when not in a grid
    white-space: nowrap;
    > span {
      margin-right: 0.25em;
    }
  }
  .dimInfo {
    opacity: 0.5;
  }
}
</style>
